// ---------------------------------------------------------------------------
// Heimdall Client Library (HCL) — Workspace-specific convenience layer
//
// Re-exports the generic @lseg-ciam/auth SDK with WS-specific defaults.
// ---------------------------------------------------------------------------

import {
  logIn as sdkLogIn,
  logOut as sdkLogOut,
  optOut as sdkOptOut,
  type LoginConfig,
  type PnsSub,
} from '@lseg-ciam/auth/login';
import {
  requestIdpToken as sdkRequestIdpToken,
  createRequestForeignIdpToken$ as sdkCreateRequestForeignIdpToken$,
  type ForeignIdpRequestOptions,
} from '@lseg-ciam/auth/link';
import {
  apiToken$ as sdkApiToken$,
  dataToken$ as sdkDataToken$,
  restrictedToken$ as sdkRestrictedToken$,
  type TokenUpdate,
  type TokenUpdateData,
} from '@lseg-ciam/auth/token';
import { estimatedServerDeltaMs, flog } from '@lseg-ciam/auth/utils';
import {
  ReplaySubject,
  EMPTY,
  catchError,
  firstValueFrom,
  map,
  share,
} from 'rxjs';
import { deleteCdnCookie$, withCdnRelay } from './cdn.js';
import { cdnFinalizeLoginHook } from './finalize.js';

const WS_CLIENT_UID = 'workspace';
const WS_LINK_URL = '/public/heimdall/link.html';

// -- SDK bulk pass-through --------------------------------------------------
// These stars must stay ABOVE HCL's own exports below. HCL intentionally
// shadows 13 SDK names (logIn/logOut/optOut, requestIdpToken/
// createRequestForeignIdpToken$, apiToken$/token$/getApiToken/getToken,
// dataToken$/restrictedToken$/getDataToken/getRestrictedToken).
// Spec ESM lets a local export shadow a star, but vite/esbuild resolves the
// ambiguity by re-export *position*: a local `const` defined AFTER the stars
// wins; a hoisted `function` decl or a `const` before the stars loses. So the
// overrides below are all `const` and all come after this block. Do not
// reorder, and do not convert them back to `function`.
export * from '@lseg-ciam/auth/login';
export * from '@lseg-ciam/auth/token';
export * from '@lseg-ciam/auth/link';
export * from '@lseg-ciam/auth/utils';

// PNS topic mapping — single source of truth for all WS packages.
export type PnsEnv = 'dev' | 'qa' | 'ppe' | 'prod';
export const PNS_TOPIC_BY_ENV: Record<PnsEnv, string> = {
  dev: 'heimdall_token_dev',
  qa: 'heimdall_token_dev',
  ppe: 'heimdall_token',
  prod: 'heimdall_token',
};

export function pnsSubForEnv(env: PnsEnv): PnsSub {
  return { e: env, t: PNS_TOPIC_BY_ENV[env] };
}

// -- logIn: default clientUid to 'workspace' for WS consumers -------------
// Accepts legacy `env` field — auto-derives `pnsSub` when not explicit.

type HclLoginConfig = Omit<LoginConfig, 'clientUid'> & {
  clientUid?: string;
  env?: PnsEnv;
};

export const logIn = (config: HclLoginConfig) => {
  const { env, ...rest } = config;
  const pnsSub = rest.pnsSub ?? (env ? pnsSubForEnv(env) : undefined);

  return sdkLogIn({
    ...rest,
    clientUid: rest.clientUid ?? WS_CLIENT_UID,
    linkUrl: rest.linkUrl ?? WS_LINK_URL,
    // default the CDN finalize hook so programmatic consumers get cookie refresh too
    onFinalizeLogin: rest.onFinalizeLogin ?? cdnFinalizeLoginHook,
    ...(pnsSub && { pnsSub }),
  } as LoginConfig);
};

// -- logOut / optOut: CDN cookie cleanup + SDK teardown ---------------------

export const logOut = (opts: { lane?: string } = {}) => {
  deleteCdnCookie$()
    .pipe(
      flog('CDN cookie cleanup'),
      catchError(() => EMPTY)
    )
    .subscribe();
  return sdkLogOut(opts);
};

export const optOut = (opts: { lane?: string } = {}) => {
  deleteCdnCookie$(true)
    .pipe(
      flog('CDN cookie cleanup (persist)'),
      catchError(() => EMPTY)
    )
    .subscribe();
  return sdkOptOut(opts);
};

// -- Link functions: default linkUrl to WS route ---------------------------

export const requestIdpToken = (
  linkUrl = WS_LINK_URL,
  options: ForeignIdpRequestOptions = {}
) =>
  sdkRequestIdpToken(linkUrl, {
    ...options,
    clientUid: options.clientUid ?? WS_CLIENT_UID,
  });

export const createRequestForeignIdpToken$ = (
  linkUrl = WS_LINK_URL,
  options: ForeignIdpRequestOptions = {}
) =>
  sdkCreateRequestForeignIdpToken$(linkUrl, {
    ...options,
    clientUid: options.clientUid ?? WS_CLIENT_UID,
  });

// positive drift is noise (network/processing latency);
const MIN_SIGNIFICANT_DRIFT_MS = 3_000;

const significantDriftMs = () => {
  const drift = estimatedServerDeltaMs();
  return drift >= 0 && drift < MIN_SIGNIFICANT_DRIFT_MS ? 0 : drift;
};

// -- expiresBy handed to clients is shifted by the observed drift so a plain
// `Date.now() > expiresBy` downstream agrees with the SDK's own internal
// `estimatedNowAtServerMs()` check. Raw expiresBy (the JWT's true `exp`) stays
// correct in absolute terms; this shift only makes it comparable to a locally
// skewed clock, which is what external consumers actually do with it.
const withDriftAdjustedExpiresBy = <T extends TokenUpdate>() =>
  map(
    (update: T): T => ({
      ...update,
      expiresBy: update.expiresBy + significantDriftMs(),
    })
  );

const sharedReplayLast = <T>() =>
  share<T>({
    connector: () => new ReplaySubject(1),
    resetOnRefCountZero: false,
  });

// -- WS apiToken$: wraps SDK stream with CDN relay (leader-elected) --------
// Shadows the SDK's `apiToken$` from the wildcard re-export above.
// Consumers of @heimdall/client get CDN cookie sync for free;
// consumers of @lseg-ciam/auth/token get the raw stream without it.
export const apiToken$ = sdkApiToken$
  .pipe(withCdnRelay())
  .pipe(withDriftAdjustedExpiresBy(), sharedReplayLast());
export const token$ = apiToken$.pipe(map(({ token }) => token));

// -- WS dataToken$ / restrictedToken$: same drift-adjusted expiresBy --------
export const dataToken$: typeof sdkDataToken$ = sdkDataToken$.pipe(
  withDriftAdjustedExpiresBy<TokenUpdateData>(),
  sharedReplayLast()
);
export const restrictedToken$: typeof sdkRestrictedToken$ =
  sdkRestrictedToken$.pipe(withDriftAdjustedExpiresBy(), sharedReplayLast());

export const getApiToken = async () => firstValueFrom(apiToken$);
export const getToken = async () => firstValueFrom(token$);
export const getDataToken = async () => firstValueFrom(dataToken$);
export const getRestrictedToken = async () => firstValueFrom(restrictedToken$);

export { VERSION } from './metadata.js';
